import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, Download, FileText } from 'lucide-react';
import { getFatturaDettaglio, getFatturaXmlBlob, getProformaEmessaPdf, type FatturaDettaglio } from '../../api/fatture';
import { fmtKg } from '../../lib/saldo';

const eur = (n: number | null) => (n == null ? '—' : `€ ${n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

interface Props {
  fatturaId: string;
  onClose: () => void;
}

export default function FatturaDettaglioModal({ fatturaId, onClose }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [d, setD] = useState<FatturaDettaglio | null>(null);
  const [error, setError] = useState<string | null>(null);

  function apriConferimento(id: string) {
    onClose();
    navigate(`/conferimenti/${id}`);
  }

  function apriUscita(id: string) {
    onClose();
    navigate(`/uscite/${id}`);
  }

  useEffect(() => {
    getFatturaDettaglio(fatturaId).then(setD).catch(() => setError(t('fatturazione.dettaglio.errore')));
  }, [fatturaId, t]);

  async function scaricaXml() {
    if (!d) return;
    const blob = await getFatturaXmlBlob(d.id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    // "1/M" → "1-M": '/' non è valido in un filename.
    a.href = url; a.download = `fattura_${(d.numero_fattura ?? d.id).replace(/\//g, '-')}.xml`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid sticky top-0 bg-white">
          <h2 className="text-base font-semibold text-ink-dark flex items-center gap-2">
            <FileText size={16} className="text-sage" /> {t('fatturazione.dettaglio.titolo', { numero: d?.numero_fattura ?? '' })}
          </h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark"><X size={18} /></button>
        </div>
        {error ? (
          <div className="px-6 py-8 text-sm text-accent-danger">{error}</div>
        ) : !d ? (
          <div className="px-6 py-8 text-sm text-ink-soft">{t('fatturazione.dettaglio.caricamento')}</div>
        ) : (
          <div className="px-6 py-5 space-y-4">
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
              <div><span className="text-ink-soft">{t('fatturazione.dettaglio.cliente')}</span> <span className="font-medium">{d.customer_ragione_sociale}</span></div>
              <div><span className="text-ink-soft">{t('fatturazione.dettaglio.data')}</span> {new Date(d.data_emissione).toLocaleDateString('it-IT')}</div>
              <div><span className="text-ink-soft">{t('fatturazione.dettaglio.n_ufficiale')}</span> {d.numero_fattura ?? '—'}</div>
              <div><span className="text-ink-soft">{t('fatturazione.dettaglio.n_interno')}</span> {d.numero_interno ?? '—'}</div>
              <div><span className="text-ink-soft">{t('fatturazione.dettaglio.progressivo')}</span> {d.progressivo_invio ?? '—'}</div>
              <div><span className="text-ink-soft">{t('fatturazione.dettaglio.stato')}</span> {t(`fatturazione.attive.stato.${d.stato}`, { defaultValue: d.stato })}</div>
              <div data-testid="dettaglio-tipo"><span className="text-ink-soft">{t('fatturazione.attive.col_tipo')}</span>{' '}
                {t(d.tipo === 'vendita' ? 'fatturazione.attive.tipo_vendita' : 'fatturazione.attive.tipo_servizio')}</div>
              <div data-testid="dettaglio-scadenza"><span className="text-ink-soft">{t('fatturazione.attive.col_scadenza')}</span>{' '}
                {d.data_scadenza ? new Date(d.data_scadenza).toLocaleDateString('it-IT') : '—'}</div>
            </div>

            {d.tipo === 'vendita' ? (
              <table className="w-full text-sm border border-stone-mid rounded-lg overflow-hidden" data-testid="dettaglio-tabella-uscite">
                <thead className="bg-stone-bg">
                  <tr className="text-left text-xs font-semibold text-ink-soft uppercase">
                    <th className="px-3 py-2">{t('fatturazione.bozza.col_uscita')}</th>
                    <th className="px-3 py-2 text-right">{t('fatturazione.dettaglio.col_peso')}</th>
                    <th className="px-3 py-2 text-right">{t('fatturazione.dettaglio.col_eur_kg')}</th>
                    <th className="px-3 py-2 text-right">{t('fatturazione.dettaglio.col_importo')}</th>
                  </tr>
                </thead>
                <tbody>
                  {/* V76 — una riga per (uscita, tipologia): due righe della
                      stessa uscita ripetono codice e FIR, perche' su quel FIR
                      ci sono salite entrambe. La chiave e' `riga_id`. */}
                  {d.uscite.map((u) => (
                    <tr key={u.riga_id} className="border-t border-stone-mid/60" data-testid={`uscita-riga-${u.riga_id}`}>
                      <td className="px-3 py-2 font-medium">
                        <button type="button" onClick={() => apriUscita(u.uscita_id)}
                          className="text-sage-dark hover:underline font-mono text-xs" data-testid={`uscita-link-${u.uscita_id}`}>
                          {u.codice}
                        </button>
                        <span className="block text-[11px] text-ink-faint font-normal">{u.descrizione_rifiuto} · {u.cer_code}</span>
                        <span className="block text-[11px] text-ink-faint font-normal" data-testid={`uscita-tipologia-${u.riga_id}`}>
                          {u.tipologia_nome ?? t('fatturazione.bozza.tipologia_non_specificata')}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">{u.peso_kg != null ? fmtKg(u.peso_kg) : '—'}</td>
                      <td className="px-3 py-2 text-right">{eur(u.prezzo_unitario)}</td>
                      <td className="px-3 py-2 text-right">{eur(u.importo_riga_eur)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
            <table className="w-full text-sm border border-stone-mid rounded-lg overflow-hidden">
              <thead className="bg-stone-bg">
                <tr className="text-left text-xs font-semibold text-ink-soft uppercase">
                  <th className="px-3 py-2">{t('fatturazione.dettaglio.col_conferimento')}</th>
                  <th className="px-3 py-2 text-right">{t('fatturazione.dettaglio.col_peso')}</th>
                  <th className="px-3 py-2 text-right">{t('fatturazione.dettaglio.col_eur_kg')}</th>
                  <th className="px-3 py-2 text-right">{t('fatturazione.dettaglio.col_importo')}</th>
                </tr>
              </thead>
              <tbody>
                {d.righe.map((r) => (
                  <tr key={r.conferimento_id} className="border-t border-stone-mid/60">
                    <td className="px-3 py-2 font-medium">
                      <button type="button" onClick={() => apriConferimento(r.conferimento_id)}
                        className="text-sage-dark hover:underline" data-testid={`conf-link-${r.conferimento_id}`}>
                        {r.numero ?? r.conferimento_id.slice(0, 8)}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right">{r.peso_kg != null ? fmtKg(r.peso_kg) : '—'}</td>
                    <td className="px-3 py-2 text-right">{eur(r.prezzo_unitario_eur_kg)}</td>
                    <td className="px-3 py-2 text-right">{eur(r.importo_riga_eur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}

            {d.voci_extra.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs font-semibold text-ink-soft uppercase">{t('fatturazione.dettaglio.voci_extra')}</div>
                <table className="w-full text-sm border border-stone-mid rounded-lg overflow-hidden">
                  <thead className="bg-stone-bg">
                    <tr className="text-left text-xs font-semibold text-ink-soft uppercase">
                      <th className="px-3 py-2">{t('fatturazione.dettaglio.col_voce')}</th>
                      <th className="px-3 py-2 text-right">{t('fatturazione.dettaglio.col_qta')}</th>
                      <th className="px-3 py-2 text-right">{t('fatturazione.dettaglio.col_prezzo_unit')}</th>
                      <th className="px-3 py-2 text-right">{t('fatturazione.dettaglio.col_importo')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.voci_extra.map((v, i) => (
                      <tr key={i} className="border-t border-stone-mid/60" data-testid="voce-extra-row">
                        <td className="px-3 py-2 font-medium">{v.descrizione}</td>
                        <td className="px-3 py-2 text-right">{v.quantita?.toLocaleString('it-IT') ?? '—'}</td>
                        <td className="px-3 py-2 text-right">{eur(v.prezzo_unitario_eur)}</td>
                        <td className="px-3 py-2 text-right">{eur(v.imponibile)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="text-sm space-y-1 border-t border-stone-mid pt-3">
              <div className="flex justify-between"><span>{t('fatturazione.dettaglio.imponibile')}</span><span>{eur(d.importo_netto)}</span></div>
              <div className="flex justify-between text-ink-soft"><span>{t('fatturazione.dettaglio.di_cui_trasporto')}</span><span>{eur(d.importo_trasporto)}</span></div>
              <div className="flex justify-between font-semibold text-base"><span>{t('fatturazione.dettaglio.totale')}</span><span>{eur(d.importo_totale)}</span></div>
            </div>

            <div className="flex items-center gap-4">
              {d.xml_disponibile && (
                <button type="button" onClick={scaricaXml} data-testid="scarica-xml-modal" className="inline-flex items-center gap-1 text-sm text-sage-dark hover:underline">
                  <Download size={14} /> {t('fatturazione.dettaglio.scarica_xml')}
                </button>
              )}
              <button type="button" onClick={() => getProformaEmessaPdf(d.id)} data-testid="scarica-proforma"
                className="inline-flex items-center gap-1 text-sm text-sage-dark hover:underline">
                <FileText size={14} /> {t('fatturazione.dettaglio.scarica_proforma')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
